import { useEffect, useState, type ReactNode } from "react";
import { Button, type ButtonProps } from "./Button";

// The Clevis rule: no browser dialogs, ever (no window.confirm). This button
// arms itself inline instead — first click swaps it for the question plus
// Yes/Cancel, auto-disarming after a few seconds of hesitation.

export interface ConfirmButtonProps extends Omit<ButtonProps, "onClick"> {
  /** The inline question shown once armed, e.g. "Disconnect QuickBooks?" */
  confirm: string;
  confirmLabel?: string;
  onConfirm: () => void | Promise<void>;
  children: ReactNode;
}

export function ConfirmButton({
  confirm,
  confirmLabel = "Yes",
  onConfirm,
  children,
  variant = "ghost",
  busy,
  className,
  ...rest
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 6000);
    return () => clearTimeout(t);
  }, [armed]);

  if (!armed) {
    return (
      <Button variant={variant} busy={busy} className={className} onClick={() => setArmed(true)} {...rest}>
        {children}
      </Button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-xs font-medium text-ink">{confirm}</span>
      <Button
        variant="danger"
        busy={busy}
        className="px-2.5 py-1 text-xs"
        onClick={() => {
          setArmed(false);
          void onConfirm();
        }}
        {...rest}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" className="px-2.5 py-1 text-xs" onClick={() => setArmed(false)}>
        Cancel
      </Button>
    </span>
  );
}
